<!-- eslint-disable vue/no-setup-props-destructure -->
<!-- eslint-disable no-undef -->
<script setup lang="ts">
import { computed } from "vue";

interface Props {
  /** Тип кнопки */
  type?: "button" | "submit" | "reset";
  /** Отключена ли кнопка */
  disabled?: boolean;
  /** Инвертированный вариант (белый фон, черная иконка) */
  inverted?: boolean;
  /** Размер кнопки */
  size?: "sm" | "md" | "lg";
}

interface Emits {
  (e: "click", event: MouseEvent): void;
}

const {
  type = "button",
  disabled = false,
  inverted = false,
  size = "md",
} = defineProps<Props>();

const emit = defineEmits<Emits>();

const variantClasses = computed(() => {
  return inverted ? "button--inverted" : "button--default";
});

const sizeClasses = computed(() => {
  return `button--${size}`;
});

const handleClick = (event: MouseEvent) => {
  if (!disabled) {
    emit("click", event);
  }
};
</script>

<template>
  <button
    :class="['button', variantClasses, sizeClasses]"
    :type="type"
    :disabled="disabled"
    @click="handleClick"
  >
    <slot></slot>
  </button>
</template>

<style lang="scss" scoped>
// SCSS Variables
$button-radius: 50%;
$button-transition-duration: 200ms;
$button-focus-ring-width: 2px;
$button-focus-ring-offset: 2px;

// Sizes
$button-sizes: (
  sm: (
    width: 32px,
    height: 32px,
  ),
  md: (
    width: 40px,
    height: 40px,
  ),
  lg: (
    width: 48px,
    height: 48px,
  ),
);

// Colors - Default variant
$button-default-bg: rgba(255, 255, 255, 0.08);
$button-default-text: currentColor;
$button-default-hover-bg: rgba(0, 0, 0, 0.05);
$button-default-focus-ring: rgba(156, 163, 175, 0.5);

// Colors - Inverted variant
$button-inverted-bg: #ffffff;
$button-inverted-text: #000000;
$button-inverted-hover-bg: #f3f4f6;
$button-inverted-focus-ring: rgba(209, 213, 219, 0.5);

// Disabled state
$button-disabled-opacity: 0.5;

.button {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $button-radius;
  border: none;
  cursor: pointer;
  transition: all $button-transition-duration ease;
  outline: none;
  padding: 0;

  &:focus {
    box-shadow: 0 0 0 $button-focus-ring-width $button-default-focus-ring,
      0 0 0 $button-focus-ring-width + $button-focus-ring-offset transparent;
  }

  &:disabled {
    opacity: $button-disabled-opacity;
    cursor: not-allowed;
  }
}

// Size variants
.button--sm {
  width: map-get(map-get($button-sizes, sm), width);
  height: map-get(map-get($button-sizes, sm), height);
}

.button--md {
  width: map-get(map-get($button-sizes, md), width);
  height: map-get(map-get($button-sizes, md), height);
}

.button--lg {
  width: map-get(map-get($button-sizes, lg), width);
  height: map-get(map-get($button-sizes, lg), height);
}

// Default variant (transparent)
.button--default {
  background-color: $button-default-bg;
  color: $button-default-text;

  &:hover:not(:disabled) {
    background-color: $button-default-hover-bg;
  }

  &:focus {
    box-shadow: 0 0 0 $button-focus-ring-width $button-default-focus-ring,
      0 0 0 $button-focus-ring-width + $button-focus-ring-offset transparent;
  }
}

// Inverted variant (white background, black icon)
.button--inverted {
  background-color: $button-inverted-bg;
  color: $button-inverted-text;

  &:hover:not(:disabled) {
    background-color: $button-inverted-hover-bg;
  }

  &:focus {
    box-shadow: 0 0 0 $button-focus-ring-width $button-inverted-focus-ring,
      0 0 0 $button-focus-ring-width + $button-focus-ring-offset transparent;
  }
}
</style>
